import { NOVA_SEASONAL_WEIGHTS } from "@/lib/marketPresets";

export interface CalcInputs {
  annualRentalIncome: number;
  annualOtherIncome: number;
  airbnbPlatformFeeRate: number;
  managementFeeRate: number;
  mortgage: number;
  utilities: number;
  maintenance: number;
  supplies: number;
  otherFees: number;
  targetOccupancy: number;
  purchasePrice: number;
  downPaymentPercent: number;
  closingCostsPercent: number;
  furnishingCost: number;
  interestRate: number;
  loanTerm: number;
  propertyTaxRate: number;
  annualInsurance: number;
}

export interface CalcResults {
  monthlyRentalIncome: number;
  totalAnnualIncome: number;
  totalMonthlyIncome: number;
  annualAirbnbPlatformFee: number;
  monthlyAirbnbPlatformFee: number;
  annualManagementFee: number;
  monthlyManagementFee: number;
  monthlyOperatingExpenses: number;
  annualOperatingExpenses: number;
  annualMortgage: number;
  annualTotalExpenses: number;
  monthlyTotalExpenses: number;
  annualNetCashFlow: number;
  monthlyNetCashFlow: number;
  annualGrossPayout: number;
  monthlyGrossPayout: number;
  monthlyBreakdown: Array<{
    month: string;
    label: string;
    weight: number;
    occFactor: number;
    status: string;
    tagColor: string;
    monthOccupancy: number;
    revenue: number;
    platformFee: number;
    mgmtFee: number;
    payout: number;
    netCashFlow: number;
  }>;
  downPaymentAmount: number;
  closingCostsAmount: number;
  totalAcquisitionCost: number;
  estimatedMortgage: number;
}

export const computeCalcResults = (i: CalcInputs): CalcResults => {
  const monthlyRentalIncome = Math.round(i.annualRentalIncome / 12);
  const totalAnnualIncome = i.annualRentalIncome + i.annualOtherIncome;
  const totalMonthlyIncome = Math.round(totalAnnualIncome / 12);

  const annualAirbnbPlatformFee =
    i.annualRentalIncome * i.airbnbPlatformFeeRate;
  const monthlyAirbnbPlatformFee = annualAirbnbPlatformFee / 12;

  const annualManagementFee = i.annualRentalIncome * i.managementFeeRate;
  const monthlyManagementFee = annualManagementFee / 12;

  const monthlyOperatingExpenses =
    i.utilities + i.maintenance + i.supplies + i.otherFees;
  const annualOperatingExpenses = monthlyOperatingExpenses * 12;

  const annualMortgage = i.mortgage * 12;

  const annualTotalExpenses =
    annualMortgage +
    annualOperatingExpenses +
    annualManagementFee +
    annualAirbnbPlatformFee;
  const monthlyTotalExpenses =
    i.mortgage +
    monthlyOperatingExpenses +
    monthlyManagementFee +
    monthlyAirbnbPlatformFee;

  const annualNetCashFlow = Math.round(totalAnnualIncome - annualTotalExpenses);
  const monthlyNetCashFlow = Math.round(annualNetCashFlow / 12);

  const annualGrossPayout =
    totalAnnualIncome - annualManagementFee - annualAirbnbPlatformFee;
  const monthlyGrossPayout = annualGrossPayout / 12;

  const sumWeights = NOVA_SEASONAL_WEIGHTS.reduce(
    (acc, w) => acc + w.weight,
    0,
  );
  const monthlyBreakdown = NOVA_SEASONAL_WEIGHTS.map((m) => {
    const rawMonthRevenue = (i.annualRentalIncome / sumWeights) * m.weight;
    const monthRevenue = Math.round(rawMonthRevenue);
    const monthPlatformFee = Math.round(monthRevenue * i.airbnbPlatformFeeRate);
    const monthMgmtFee = Math.round(monthRevenue * i.managementFeeRate);
    const monthPayout = monthRevenue - monthPlatformFee - monthMgmtFee;
    const monthNetCashFlow =
      monthPayout - i.mortgage - monthlyOperatingExpenses;
    const monthOccupancy = Math.min(
      98,
      Math.round(i.targetOccupancy * m.occFactor),
    );
    return {
      ...m,
      monthOccupancy,
      revenue: monthRevenue,
      platformFee: monthPlatformFee,
      mgmtFee: monthMgmtFee,
      payout: monthPayout,
      netCashFlow: monthNetCashFlow,
    };
  });

  const downPaymentAmount = i.purchasePrice * (i.downPaymentPercent / 100);
  const closingCostsAmount = i.purchasePrice * (i.closingCostsPercent / 100);
  const totalAcquisitionCost =
    downPaymentAmount + closingCostsAmount + i.furnishingCost;

  const principal = i.purchasePrice - downPaymentAmount;
  const monthlyInterestRate = i.interestRate / 100 / 12;
  const numberOfPayments = i.loanTerm * 12;

  const monthlyPrincipalAndInterest =
    monthlyInterestRate > 0
      ? (principal *
          monthlyInterestRate *
          Math.pow(1 + monthlyInterestRate, numberOfPayments)) /
        (Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1)
      : principal / numberOfPayments;

  const monthlyTax = (i.purchasePrice * (i.propertyTaxRate / 100)) / 12;
  const monthlyInsurance = i.annualInsurance / 12;

  const estimatedMortgage =
    monthlyPrincipalAndInterest + monthlyTax + monthlyInsurance;

  return {
    monthlyRentalIncome,
    totalAnnualIncome,
    totalMonthlyIncome,
    annualAirbnbPlatformFee,
    monthlyAirbnbPlatformFee,
    annualManagementFee,
    monthlyManagementFee,
    monthlyOperatingExpenses,
    annualOperatingExpenses,
    annualMortgage,
    annualTotalExpenses,
    monthlyTotalExpenses,
    annualNetCashFlow,
    monthlyNetCashFlow,
    annualGrossPayout,
    monthlyGrossPayout,
    monthlyBreakdown,
    downPaymentAmount,
    closingCostsAmount,
    totalAcquisitionCost,
    estimatedMortgage,
  };
};
